// messagebox-ui.ts — the in-game MESSAGE BOX overlay (domMessageBox), the HtMessageBox family
// (alerts / warnings / errors / Yes-No queries) the session raises via env.domMessageBox — the
// resign/leave WARNING (AskResignGame), the GAME OVER observe query, the MP comm/server-message
// errors, the objectives alert. DISPLAY-only: the view reports the user's OK/Yes/No through done()
// and the host routes it to the SAME callback the canvas DoModal uses (no Message constructed here).
// Lifted verbatim from main.ts's mountMessageBox, a sibling of command-ui.ts / hud-seams.ts /
// objectives-ui.ts / ecom-ui.ts. The mission editor's Play host uses it now; main.ts's inline
// mountMessageBox migrates onto it too, so they converge with no drift.
//
// NOTE: this is the SHARED, modal singleton box (#dom-messagebox). main.ts's DEDICATED, modeless
// MP "LAG ALERT" box (#dom-lagbox) rides its OWN MessageBoxView in main.ts and is NOT part of this
// module (it must coexist with / outlive a box this singleton would replace).

import { MessageBoxView } from './message-box-view.ts';
import { extractButtonArt, type ButtonArt } from '../title-art.ts';
import type { TexAtlasMgr } from '../../gfx/texatlasmgr.ts';

export interface MessageBoxUiDeps {
  /** the texture atlas mgr — sources the 9-slice button frame (extractButtonArt). */
  tam: TexAtlasMgr;
  /** optional pre-extracted 9-slice button art; else extracted once via extractButtonArt(tam). */
  objBtnArt?: ButtonArt | null;
  /** play a GUI sfx on a button press, wired to the host SoundMgr (no-op headless). */
  playSfx: (sfx: number) => void;
  /** the element the overlay root mounts into (default document.body). The root is
   *  `position:fixed; inset:0`; a TRANSFORMED pane-covering host element bounds it to the
   *  pane instead of the window (the mission editor's play-in-place WYSIWYG mode). */
  host?: HTMLElement;
}

export interface MessageBoxUi {
  /** the domMessageBox session opt (gameSessionOpts.domMessageBox) — HtMessageBox calls this with
   *  the title/message/variant; `done(fOk)` runs the SAME continuation the canvas DoModal used. */
  domMessageBox: (
    title: string,
    message: string,
    variant: 'ok' | 'query',
    whiteBorder: boolean,
    done: (fOk: boolean) => void,
  ) => void;
  /** idempotently unmount the box if it's up (game-over / host close). */
  teardown(): void;
}

/** Stand up the shared in-game message-box overlay for one play session. */
export function createMessageBoxUI(deps: MessageBoxUiDeps): MessageBoxUi {
  const { tam, playSfx } = deps;
  const objBtnArt: ButtonArt | undefined = deps.objBtnArt ?? extractButtonArt(tam) ?? undefined;

  const root = document.createElement('div');
  root.id = 'dom-messagebox';
  root.style.display = 'none';
  (deps.host ?? document.body).appendChild(root);
  let view: MessageBoxView | null = null;

  const unmount = (): void => {
    root.style.display = 'none';
    view?.destroy();
    view = null;
    root.replaceChildren();
  };

  const domMessageBox = (
    title: string,
    message: string,
    variant: 'ok' | 'query',
    whiteBorder: boolean,
    done: (fOk: boolean) => void,
  ): void => {
    unmount(); // destroy any prior box first (no leaked listeners/dropped done) — matches mountBuildPanel
    root.style.display = 'block';
    view = new MessageBoxView({
      root,
      title,
      message,
      variant,
      whiteBorder,
      buttonArt: objBtnArt,
      playSfx: (sfx: number) => {
        playSfx(sfx);
      },
      // report the choice: unmount FIRST, then run the continuation (the SAME `done` the canvas
      // HtMessageBox calls — resign/observe/error close) so a follow-on modal isn't behind us.
      onResult: (fOk: boolean) => {
        unmount();
        done(fOk);
      },
    });
  };

  return { domMessageBox, teardown: unmount };
}
